<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="5">
            <FormulateInput
              id="name"
              v-model="filters.name"
              :label="$t('Nome')"
              :placeholder="$t('Nome')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="acquirer"
              v-model="filters.acquirer"
              type="vue-select"
              :label="$t('Adquirente')"
              :placeholder="$t('Todas')"
              :options="acquirerOptions()"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="status"
              v-model="filters.status"
              type="vue-select"
              :label="$t('Status')"
              :placeholder="$t('Todos')"
              :options="activeInactiveTypes()"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        ref="acquirer-cost-configs-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($t('Configuração de custo'))"
        :fields="fields"
        :items="costConfigs"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :show-update="true"
            :show-delete="true"
            @update="updateCostConfig(row)"
            @delete="deleteCostConfigLocal(row.item)"
          />
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="acquirer-cost-configs-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <fab
      :main-tooltip="$t('Adicionar configuração de custo')"
      @click="createCostConfig"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EPageSizeSelector, EPaginationSummary } from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { cardDomain, statusTypes } from '@/mixins'
import EStatusBadge from '@/views/components/EStatusBadge.vue'
import EFilters from '@/views/components/EFilters.vue'
import EGridActions from '@/views/components/EGridActions.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EStatusBadge,
    EFilters,
    EGridActions,
  },

  mixins: [statusTypes, cardDomain],

  data() {
    return {
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/settings/financial/acquirerCostConfig', {
      costConfigs: 'costConfigs',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          class: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Adquirente'),
          key: 'acquirer',
          class: 'text-center',
          thStyle: { width: '140px' },
          sortable: true,
        },
        // Presencial e online na mesma célula. São seis percentuais ao todo; uma coluna para cada
        // deixaria a grade ilegível, e separá-los esconderia justamente a comparação que importa.
        {
          label: this.$t('Operação à vista'),
          key: 'physical.operationCostCashPercent',
          class: 'text-right',
          thStyle: { width: '170px' },
          formatter: (value, key, item) =>
            this.formatChannelPercents(item, 'operationCostCashPercent'),
        },
        {
          label: this.$t('Operação parcelado'),
          key: 'physical.operationCostInstallmentPercent',
          class: 'text-right',
          thStyle: { width: '180px' },
          formatter: (value, key, item) =>
            this.formatChannelPercents(item, 'operationCostInstallmentPercent'),
        },
        {
          label: this.$t('Antecipação ao mês'),
          key: 'physical.anticipationCostMonthlyPercent',
          class: 'text-right',
          thStyle: { width: '180px' },
          formatter: (value, key, item) =>
            this.formatChannelPercents(item, 'anticipationCostMonthlyPercent'),
        },
        {
          label: this.$t('Status'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
      ]
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    ...mapActions('pages/settings/financial/acquirerCostConfig/acquirerCostConfigMaintain', {
      stCleanStateOfMaintain: 'cleanState',
    }),
    ...mapActions('pages/settings/financial/acquirerCostConfig', [
      'fetchCostConfigs',
      'deleteCostConfig',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),

    /**
     * Os percentuais vêm da API como percentual, não como fração: 1.79 é 1,79%.
     * Multiplicar por 100 aqui produziria 179%.
     */
    /**
     * Percentual presencial e online lado a lado, na ordem em que a tela de cadastro os mostra.
     */
    formatChannelPercents(item, field) {
      const physical = this.formatPercent(item?.physical?.[field])
      const online = this.formatPercent(item?.online?.[field])

      return `${physical} / ${online}`
    },

    formatPercent(value) {
      if (value === null || value === undefined) return ''
      return `${Number(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      })}%`
    },

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchCostConfigs()
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },

    filterData() {
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    updateCostConfig(row) {
      const { id } = row.item
      this.$router.push({ name: 'acquirer-cost-config-maintain', params: { id } })
    },

    createCostConfig() {
      this.stCleanStateOfMaintain()
      this.$router.push({ name: 'acquirer-cost-config-add' })
    },

    /**
     * A API recusa a exclusão quando a configuração está vinculada a alguma política, informando
     * quais. A mensagem devolvida é exibida como está: apagar a configuração deixaria políticas
     * sem base de custo.
     */
    async deleteCostConfigLocal(costConfig) {
      try {
        const confirm = await this.confirm()
        if (!confirm) return

        await this.deleteCostConfig(costConfig.id)
        this.getData()
        this.showSuccess({ message: this.$t('Configuração de custo excluída com sucesso') })
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
